import axios from 'axios'
import audioAnalysisService from './audioAnalysisService'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'
// Railway backend for audio downloads (no timeout limits)
const RAILWAY_API_URL = import.meta.env.VITE_RAILWAY_API_URL || API_BASE_URL

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Search SoundCloud tracks
export const searchTracks = async (query, genre = 'all') => {
  try {
    const response = await api.get('/search', {
      params: { q: query, genre, limit: 20 }
    })

    // Log response for debugging
    console.log('Search API response type:', typeof response.data, 'isArray:', Array.isArray(response.data))

    // If API returned error object instead of array, return it so frontend can display error
    if (!Array.isArray(response.data)) {
      console.error('API returned non-array:', response.data)
      return response.data // Let frontend handle the error object
    }

    return response.data
  } catch (error) {
    console.error('Search error:', error)
    console.error('Error response:', error.response?.data)
    throw error
  }
}

// Analyze a track using REAL Web Audio API analysis
export const analyzeTrack = async (trackId, trackUrl) => {
  try {
    console.log('Analyzing track with REAL audio analysis:', trackId)

    // Use Railway backend to download audio (no Vercel timeout limits)
    const downloadUrl = `${RAILWAY_API_URL}/api/download-audio/${trackId}`

    // Use real audio analysis service
    const analysis = await audioAnalysisService.analyzeTrack(trackId, downloadUrl)

    return analysis
  } catch (error) {
    console.error('Analysis error:', error)
    throw error
  }
}

// Get recommendations for a track using REAL vibe-based compatibility scoring
export const getRecommendations = async (trackId, sourceAnalysis, onProgress = null) => {
  try {
    console.log('Getting recommendations with REAL vibe-based compatibility scoring')
    console.log('Source analysis:', sourceAnalysis)

    // Determine genre from BPM
    let genre = 'drum and bass'
    if (sourceAnalysis.bpm >= 170 && sourceAnalysis.bpm <= 185) {
      genre = 'drum and bass'
    } else if (sourceAnalysis.bpm >= 135 && sourceAnalysis.bpm <= 145) {
      genre = 'dubstep'
    } else if (sourceAnalysis.bpm >= 155 && sourceAnalysis.bpm <= 170) {
      genre = 'jungle'
    }

    // Get candidate tracks from backend with BPM/genre context
    console.log(`Requesting recommendations: BPM=${sourceAnalysis.bpm}, Genre=${genre}`)
    const response = await api.get(`/recommendations/${trackId}`, {
      params: {
        bpm: sourceAnalysis.bpm,
        genre
      }
    })
    const candidates = response.data

    console.log(`Got ${candidates.length} candidates from diverse searches`)

    if (candidates.length === 0) {
      console.error('❌ BACKEND RETURNED 0 CANDIDATES!')
      console.error('This means searches failed or all results were filtered.')
      console.error('Check Vercel function logs at: vercel.com → Project → Deployments → Functions')
      console.error(`Request was: /api/recommendations/${trackId}?bpm=${sourceAnalysis.bpm}&genre=${genre}`)
      // Don't throw - just return empty array
      return []
    }

    // Analyze in SMALL batches to prevent browser freezing
    // CRITICAL: 20 parallel MP3 downloads/analyses = browser killer!
    let allAnalyzed = []
    let batchSize = 3 // Only 3 at a time (was 20 - way too heavy!)
    let compatibleTracks = []
    const minCompatibility = 50 // Lower threshold from 70% to 50%

    for (let batchStart = 0; batchStart < candidates.length && compatibleTracks.length < 5; batchStart += batchSize) {
      const batch = candidates.slice(batchStart, batchStart + batchSize)
      const batchNum = Math.floor(batchStart / batchSize) + 1

      console.log(`\n=== BATCH ${batchNum}: Analyzing ${batch.length} tracks (${batchStart + 1}-${batchStart + batch.length} of ${candidates.length}) ===`)

      const total = batch.length
      let completed = 0

      // Analyze tracks in PARALLEL for speed
      const analyzedBatch = await Promise.all(
        batch.map(async (rec) => {
          try {
            const downloadUrl = `${RAILWAY_API_URL}/api/download-audio/${rec.track.id}`
            const candidateAnalysis = await audioAnalysisService.analyzeTrack(rec.track.id, downloadUrl)
            const realCompatibility = audioAnalysisService.calculateCompatibility(sourceAnalysis, candidateAnalysis)

            // Log compatibility scores for debugging
            console.log(`✓ ${rec.track.title}: ${realCompatibility}% compatible (BPM: ${candidateAnalysis.bpm}, Key: ${candidateAnalysis.key})`)

            completed++
            if (onProgress) {
              onProgress({
                completed: batchStart + completed,
                total: Math.min(candidates.length, batchStart + batchSize),
                currentTrack: rec.track.title,
                percentage: Math.round(((batchStart + completed) / Math.min(candidates.length, batchStart + batchSize)) * 100)
              })
            }

            return { track: rec.track, compatibility: realCompatibility, analysis: candidateAnalysis }
          } catch (error) {
            console.error(`✗ Failed to analyze "${rec.track.title}":`, error.message)
            completed++
            if (onProgress) {
              onProgress({
                completed: batchStart + completed,
                total: Math.min(candidates.length, batchStart + batchSize),
                currentTrack: rec.track.title,
                percentage: Math.round(((batchStart + completed) / Math.min(candidates.length, batchStart + batchSize)) * 100),
                failed: true
              })
            }
            return null
          }
        })
      )

      // Add successful analyses to our collection
      allAnalyzed.push(...analyzedBatch.filter(rec => rec !== null))

      // Filter for compatible tracks (>= 50%)
      compatibleTracks = allAnalyzed
        .filter(rec => rec.compatibility >= minCompatibility)
        .sort((a, b) => b.compatibility - a.compatibility)

      console.log(`Batch ${batchNum} complete: Found ${compatibleTracks.length} compatible tracks so far (>=${minCompatibility}%)`)

      // Stop if we found enough good matches (reduced from 10 to 5 for speed)
      if (compatibleTracks.length >= 5) {
        console.log(`\n✓ Found ${compatibleTracks.length} compatible tracks - stopping search`)
        break
      }

      // Continue to next batch if no matches yet
      if (compatibleTracks.length === 0 && batchStart + batchSize < candidates.length) {
        console.log(`No compatible tracks yet, continuing to next batch...`)
      }

      // Small delay between batches to prevent browser freezing (500ms)
      if (batchStart + batchSize < candidates.length) {
        console.log('Waiting 500ms before next batch to prevent browser freeze...')
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }

    console.log(`\n=== FINAL RESULTS ===`)
    console.log(`Analyzed ${allAnalyzed.length} tracks total`)
    console.log(`Found ${compatibleTracks.length} compatible tracks (>=${minCompatibility}%):`)
    compatibleTracks.slice(0, 5).forEach(rec => {
      console.log(`  - ${rec.track.title} by ${rec.track.artist}: ${rec.compatibility}% (${rec.analysis.bpm} BPM, ${rec.analysis.key})`)
    })

    return compatibleTracks
  } catch (error) {
    console.error('Recommendations error:', error)
    throw error
  }
}

// Get stream URL for a track
export const getStreamUrl = async (trackId) => {
  try {
    const response = await api.get(`/stream/${trackId}`)
    return response.data.streamUrl
  } catch (error) {
    console.error('Stream URL error:', error)
    throw error
  }
}

// Blend Bank operations
export const getBlendBank = async () => {
  try {
    const response = await api.get('/blend-bank')
    return response.data
  } catch (error) {
    console.error('Get blend bank error:', error)
    throw error
  }
}

export const saveBlend = async (blendData) => {
  try {
    const response = await api.post('/blend-bank', blendData)
    return response.data
  } catch (error) {
    console.error('Save blend error:', error)
    throw error
  }
}

export const deleteBlend = async (blendId) => {
  try {
    const response = await api.delete(`/blend-bank/${blendId}`)
    return response.data
  } catch (error) {
    console.error('Delete blend error:', error)
    throw error
  }
}

export default api
